import { z } from 'zod';
import { apiEnvelope } from '../envelope';
import { aiAvailabilitySchema } from '../consents/ai-availability';
import { careTeamPhotoUrlShape } from '../care/care-photo-url';
import {
  initialReviewCalendarDateSchema,
  initialReviewStateSchema,
} from '../initial-review/schema';
import { memberPlanKindSchema } from '../plans/schema';
import { ianaTimezoneSchema } from '../timezone';
import { checkInContextGroupSummarySchema } from '../check-ins/schema';

// Wire schema for `GET /api/v1/home/summary` — the wayfinder home aggregation,
// owned server-side (B-home only consumes it). All fields derive from
// activity_events / observations / reward_ledger_entries.

/** Today's logged values for one program (main-program first in the array). */
export const homeTodayProgramSchema = z.object({
  memberProgramId: z.string(),
  programKey: z.string(),
  // The programme's single sub-type tag (`ecigarettes`), or null when it has
  // none or more than one. Optional so a bundle keeps parsing a summary from an
  // API deployed before the field (ADR-135); every shipped bundle's item object
  // is non-strict, so an older bundle drops it unread.
  subtypeTag: z.string().nullable().optional(),
  values: z.record(z.string(), z.unknown()),
});
export type HomeTodayProgram = z.infer<typeof homeTodayProgramSchema>;

/**
 * One program's reconstructed contribution to a checked-in day. Carried so an
 * edit can round-trip the whole day without collapsing a poly-program check-in:
 * the client re-sends every entry (the edited one plus the untouched others),
 * keyed by `memberProgramId`, instead of one summed quantity attributed to a
 * single program.
 */
export const checkedInEntrySchema = z.object({
  memberProgramId: z.string(),
  programKey: z.string(),
  quantity: z.number().int().nonnegative(),
  isFree: z.boolean(),
});
export type CheckedInEntry = z.infer<typeof checkedInEntrySchema>;

/**
 * One `YYYY-MM-DD` day the member checked in, with its full drink status.
 * `quantity` + `isFree` are the day-level rollup the calendar colours a cell
 * from (drink-free vs drinks-logged); `entries` is the per-program breakdown
 * that preserves a poly-program day through an edit. Values reconstruct the day
 * latest-wins over any same-day re-submits.
 */
export const checkedInDayStatusSchema = z.object({
  date: z.string(),
  quantity: z.number().int().nonnegative(),
  isFree: z.boolean(),
  entries: z.array(checkedInEntrySchema),
});
export type CheckedInDayStatus = z.infer<typeof checkedInDayStatusSchema>;

export const homeInitialReviewSummarySchema = z
  .object({
    state: initialReviewStateSchema,
    completedConversationCount: z.number().int().nonnegative(),
    requiredConversationCount: z.number().int().positive(),
    entryVisible: z.boolean(),
    sourcePlanKind: memberPlanKindSchema.optional(),
    reviewDate: initialReviewCalendarDateSchema.nullable().optional(),
    timezone: ianaTimezoneSchema.nullable().optional(),
    reviewId: z.string().uuid().nullable().optional(),
  })
  .strict();
export type HomeInitialReviewSummary = z.infer<typeof homeInitialReviewSummarySchema>;

/**
 * One tag group recorded on today's check-in: the opaque group key the client
 * echoed back on the write, and the resolved display strings stored against it.
 *
 * `tags` are display strings, not slugs — the check-in write stores what the
 * served catalog resolved, which is why retiring a group from the catalog can
 * never orphan a member's recorded tags.
 *
 * ALIASED to `checkInContextGroupSummarySchema` rather than re-declared. The two
 * were byte-identical, and the check-ins schema is the owner: it is what
 * `orderedContextGroups` returns and what the check-in day payload carries for
 * an arbitrary date. Re-declaring an identical object here would let a field
 * added to one shape silently leave the other behind — the exact drift
 * `memberCustomTagResolvedSchema` is aliased to avoid one file over. Sharing the
 * object makes them incapable of diverging instead of merely expected not to,
 * which is why the equality test beside this is free to assert BEHAVIOUR (do the
 * two surfaces report the same groups, in the same order, for today) rather than
 * re-asserting a shape the type system now guarantees.
 *
 * The import direction is deliberate and safe: this is a relative sibling import
 * inside `packages/shared`, the same form this file already uses for
 * `../envelope`, `../initial-review/schema`, `../plans/schema` and
 * `../timezone`. It is NOT the apps/api `home → check-ins` import that took the
 * home Lambda down at cold start — that was `home-service.ts` reaching
 * `context-catalog`, whose module scope resolves the i18n catalog directory and
 * throws when it is absent. This chain is zod plus relative siblings and touches
 * no filesystem, config, or node builtin; the resolved-import-graph guard in
 * `apps/api/src/home/runtime/catalog-free-bundle.contract.test.ts` now walks
 * `@galapagos/*` workspace specifiers so that claim is enforced rather than
 * asserted here.
 */
export const homeTodayContextGroupSchema = checkInContextGroupSummarySchema;
export type HomeTodayContextGroup = z.infer<typeof homeTodayContextGroupSchema>;

/**
 * The member's current plan, projected for the home plan card.
 *
 * One object for both plan kinds so the card reads the same way in the
 * onboarding (foundation) week and every recurring week. Dates are member-local
 * ISO calendar dates in the plan's frozen `timezone` (ADR-116 Rule 2), `endsOn`
 * exclusive as stored. Counts are window-scoped to `[startsOn, endsOn)` — the
 * server decides the window, the client only renders.
 *
 * `conversations.semantics` tells the card how to read `required`: `target` is
 * a goal that unlocks the review (foundation), `floor` is a minimum the count
 * keeps rising past (weekly). `reviewEntry` mirrors the review-eligibility state
 * the entry flag exposes; `hidden` is the only state without a call to action.
 * `endsOnLabel` is a server-formatted display string so the bundled on-device
 * author and the API assembler compose identical cards without either
 * formatting a date.
 *
 * Non-strict throughout (ADR-135 Rule 1): the mobile client parses this
 * response, and the projection is expected to grow (an LLM-summarised focus,
 * the profile and commit reuse), so an installed bundle must strip fields it
 * does not know rather than reject the whole summary. Adding the field to the
 * existing summary rather than a new endpoint is the ADR-135 Rule 2 carve-out,
 * accepted knowingly: every field the client parses here has landed the same
 * way, the home summary's live read goes through the tolerant parser, and the
 * persisted-snapshot path validates with the same non-strict schema.
 */
export const planProgressReviewEntryStateSchema = z.enum(['hidden', 'start', 'continue']);
export type PlanProgressReviewEntryState = z.infer<typeof planProgressReviewEntryStateSchema>;

export const planProgressConversationSemanticsSchema = z.enum(['target', 'floor']);

export const planProgressSonaStatusSchema = z.enum(['on', 'off']);
export type PlanProgressSonaStatus = z.infer<typeof planProgressSonaStatusSchema>;
export type PlanProgressConversationSemantics = z.infer<
  typeof planProgressConversationSemanticsSchema
>;

export const planProgressSchema = z.object({
  planKind: memberPlanKindSchema,
  /**
   * The Nth weekly plan (the first weekly plan is week 1). `null` for the
   * starter (`foundation`) plan, which is not a week and carries no ordinal.
   */
  weekOrdinal: z.number().int().positive().nullable(),
  startsOn: initialReviewCalendarDateSchema,
  /** Exclusive end of a weekly plan; `null` for the starter plan, which has no end date. */
  endsOn: initialReviewCalendarDateSchema.nullable(),
  timezone: ianaTimezoneSchema,
  /** Whole member-local days until `endsOn`, clamped at zero; `null` when the plan has no end. */
  daysLeft: z.number().int().nonnegative().nullable(),
  /** Display string for the plan's end (weekday + day, plan zone), server-formatted; `null` when the plan has no end. */
  endsOnLabel: z.string().min(1).nullable(),
  /**
   * The full weekday name of the day the plan's review opens (`endsOn` in the
   * plan zone), server-formatted like `endsOnLabel` — the card names it when
   * the review is still ahead ("Sunday · your weekly review"). Optional on the
   * wire so a bundle that knows the field still parses a summary from an API
   * that predates it; the card derives the name from `endsOn` when it is absent.
   * Never sent for the starter plan.
   */
  reviewWeekdayLabel: z.string().min(1).optional(),
  /**
   * Days tracked: check-in days inside a weekly plan's window, or — for the
   * starter plan, which has no window — inside the current week-strip week.
   * Imported history never counts.
   */
  daysTracked: z.number().int().nonnegative(),
  conversations: z.object({
    completed: z.number().int().nonnegative(),
    required: z.number().int().positive(),
    semantics: planProgressConversationSemanticsSchema,
  }),
  /** The week's focus line (the rank-1 committed theme), or null when the plan carries none. */
  focus: z.object({ title: z.string().min(1) }).nullable(),
  reviewEntry: z.object({
    state: planProgressReviewEntryStateSchema,
    tool: z.literal('open_weekly_plan_review'),
  }),
  /**
   * Whether Sona is part of this plan's week. `off` only on the starter plan
   * of a member whose consent is declined or whose intake is tracking-only:
   * the card frames week one around tracking. A server restriction sets
   * `canEnable: false` so it offers no reversible opt-out message or control.
   * A weekly plan is always `on` (its review has happened). Optional on the
   * wire so an installed bundle that predates it strips the field and renders
   * the consenting card (ADR-135 Rule 1).
   */
  sona: z
    .object({
      status: planProgressSonaStatusSchema,
      /** False when server policy forbids Sona; omit the reversible opt-out row. */
      canEnable: z.boolean().optional(),
    })
    .optional(),
  /**
   * The member-local calendar date (plan zone) the week-one recap is promised
   * for under Sona off: first Home arrival plus the foundation timeout. Only
   * sent for the starter plan under Sona off once the arrival is known.
   */
  recapOn: initialReviewCalendarDateSchema.optional(),
  /** The full weekday name of `recapOn`, server-formatted like `reviewWeekdayLabel`. */
  recapWeekdayLabel: z.string().min(1).optional(),
});
export type HomePlanProgress = z.infer<typeof planProgressSchema>;

export const PROGRAMME_ENROLMENT_STATES = ['active', 'ended', 'none'] as const;
export type ProgrammeEnrolment = (typeof PROGRAMME_ENROLMENT_STATES)[number];

export const homeSummaryDataSchema = z.object({
  weekly: z.object({
    checkInCount: z.number().int().nonnegative(),
    daysTracked: z.number().int().nonnegative(),
  }),
  // Consecutive calendar days with any check-in, ending at/near today. A run is
  // "current" only while the latest check-in is within two days; a >=3-day gap
  // resets it to 0. Computed server-side over full history.
  currentStreak: z.number().int().nonnegative(),
  todayByProgram: z.array(homeTodayProgramSchema),
  // The moods recorded on today's check-in, in the member's selection order.
  // Resolved server-side from the latest same-day `checked-in` payload — the
  // served `mood` tag group when present, else the legacy `moodAfter`/
  // `moodBefore` scalar — so the client never has to know that precedence
  // (ADR-015). Empty when today has no check-in or carries no mood.
  //
  // Optional (not just defaulted) for the same additive-compat reason as
  // `checkedInDayStatus` and `appointment`: a client build carrying this schema
  // must keep parsing a response from an API deployed before the field existed.
  // A deployed server always sends it (possibly empty).
  todayMoods: z.array(z.string()).optional(),
  // Today's recorded context tag groups, mirroring the stored check-in `context`
  // in the CATALOG'S SERVED GROUP ORDER (not the order the client happened to
  // write the record's keys in), with each group's tags in the member's
  // selection order. Groups whose tag list is empty are omitted; a group the
  // member recorded that the catalog no longer serves is appended after the
  // served ones, so retiring a group cannot hide history.
  //
  // Carries EVERY served group, `mood` included. The mood group is not stripped
  // here because this field is a faithful mirror of what was stored, and the one
  // consumer that must exclude it — the "surrounding" summary card — is a
  // presentation rule living in the shared surface author, where both callers
  // inherit it (ADR-015: the client names one group via
  // `CHECK_IN_MOOD_GROUP_KEY` and enumerates none of the others, so a group
  // added to the catalog tomorrow needs no client change).
  //
  // It does NOT supersede `todayMoods`, which stays the mood card's source: that
  // field resolves the served-group-else-legacy-scalar precedence server-side,
  // which a raw `context` mirror cannot express.
  //
  // Optional for the same additive-compat reason as `todayMoods` — a shipped app
  // build must keep parsing a response from an API deployed before the field
  // existed. A deployed server always sends it (possibly empty).
  todayContextGroups: z.array(homeTodayContextGroupSchema).optional(),
  // Today's check-in note — member-authored free text, served verbatim. `null`
  // when today has no check-in or that check-in recorded no note.
  //
  // Optional AND nullable, and the two mean different things: absent is "this
  // API predates the field", `null` is "there is no note". Optional for the same
  // additive-compat reason as the fields above.
  todayNote: z.string().nullable().optional(),
  pointsBalance: z.number(),
  // Whether the member has a rewards enrolment row — row existence only, read
  // without enrolling or consulting the eligibility gate, so it never changes
  // as a side effect of being asked. Clients decide whether to show rewards
  // surfaces from it through `showsRewardsSurfaces`.
  //
  // Optional because it can genuinely be absent: an API deployed before the
  // field omits it, and so does a home snapshot persisted by an earlier build.
  // Absent is not the same as `false` — it means "not known to be unenrolled".
  rewardsEnrolled: z.boolean().optional(),
  // The member's checked-in days over the requested range (`?from`/`?to`,
  // default trailing four weeks), oldest first: each day's drink rollup
  // (quantity + drink-free flag) and per-program `entries`. The single source
  // for every reader. Its dates drive the week-strip dots and "checked in
  // today"; the rollup colours the profile calendar; the entries prefill an
  // edit.
  checkedInDayStatus: z.array(checkedInDayStatusSchema),
  // Whether today is the member's first day, resolved server-side in their own
  // stored zone from when their onboarding began.
  //
  // Here rather than only on the composed home document because the bundled
  // on-device author needs it too. The device can resolve neither the member's
  // zone nor their onboarding instant, so without this it answered `false` and a
  // first-day member fell back to the ordinary-day home — losing the welcome
  // artwork and their first-conversation card — for as long as the served
  // document was superseded. It is member state, and this is where the host
  // reads member state.
  //
  // Optional for the same additive-compat reason as `checkedInDayStatus` and
  // `appointment`: a client build carrying this schema must keep parsing a
  // response from an API deployed before the field existed. Absent reads as
  // "not known to be the first day", which is the same safe answer the device
  // used to assume — a member is greeted as new only on positive evidence.
  dayZero: z.boolean().optional(),
  // Whether the member's first Sona conversation actually agreed a plan — a
  // persisted `set_weekly_plan` call by the intake agent, inside the member's
  // visibility. Separate from `dayZero` on purpose: day 0 says when the journey
  // began, not whether the conversation the first-day recap describes happened,
  // and a stalled or abandoned intro reaches Home on day 0 all the same.
  //
  // Optional in both directions (ADR-135): `.optional()` lets a newer client
  // parse an API that predates the field, and this object staying non-strict is
  // what lets an older client parse a response that carries it. Absent reads as
  // "not known to have agreed", so the recap is withheld rather than claimed —
  // the same positive-evidence-only posture as `dayZero`.
  firstConversationPlanAgreed: z.boolean().optional(),
  // Conditional "Book a call" home card. `visible` is true when a completed
  // member-led conversation exists for the member AND no care-call booking has been set
  // (server-derived; independent of the captured interest signal). `slotDate` is
  // the raw `YYYY-MM-DD` offered slot — data, not copy. The card's section
  // heading and human-readable slot label are rendered client-side (heading from
  // the locales catalog, the date locale-formatted), so this endpoint stays
  // uncontracted for locale negotiation (ADR-017). The client only renders the
  // card when `visible`.
  bookACall: z.object({
    visible: z.boolean(),
    slotDate: z.string(),
  }),
  // A confirmed upcoming care-team appointment, or null when none is set. When
  // present the home screen renders the "Upcoming appointment" card (and the
  // mutually-exclusive "Book a call" card is suppressed). `scheduledFor` is an
  // ISO-8601 instant and `providerName` a display string — data, not copy; the
  // date/time are locale-formatted client-side (ADR-017).
  // Optional (not just nullable) so a client that adds this field keeps working
  // against a backend deployed before it existed — the field is simply absent
  // then, and the home renders no appointment card (same additive-compat rule as
  // `checkedInDayStatus`). A deployed server always sends it (object or null).
  appointment: z
    .object({
      providerName: z.string(),
      scheduledFor: z.string(),
      // The scheduling row's id, so the card can deep-link into call details.
      // Optional (additive compat): a response from an API deployed before the
      // field existed simply omits it, and the card renders without a link.
      appointmentId: z.string().optional(),
      durationMinutes: z.number().int().positive().optional(),
      /** Optional so a new client tolerates an old server that omits the field (ADR-135 Rule 6). */
      joinPromotedFrom: z.string().optional(),
      /** Routable call type when the booking row carries one; drives the pill label (ADR-017). */
      callType: z.string().optional(),
      /** The specialist's published photo for the card avatar; absent means initials (ADR-135 Rule 6). */
      providerPhotoUrl: z
        .string()
        .regex(careTeamPhotoUrlShape, 'providerPhotoUrl must be an https URL with a host')
        .optional(),
    })
    .nullable()
    .optional(),
  // Server-gated BAM home-entry card. `visible` is resolved server-side based on
  // whether the member has completed the assessment recently (ADR-079). The card's copy is NOT carried
  // here — like every other home card it is resolved from the locale catalog by
  // the catalog-aware surface layer (ADR-017), so the summary stays copy-free.
  // Optional for the same additive-compat reason as the fields above — an older
  // client simply ignores it, and the bundled offline-fallback home omits it
  // entirely. The client renders the card only when `visible`.
  bamHomeCard: z
    .object({
      visible: z.boolean(),
    })
    .optional(),
  // Additive and fail-closed: older APIs omit it; any initial-review read or
  // validation failure also omits it so Home preserves its pre-review shape.
  initialReview: homeInitialReviewSummarySchema.optional(),
  // The plan card's projection. Additive and fail-closed like `initialReview`:
  // older APIs omit it, and any plan/review read failure omits it so Home renders
  // no card rather than a wrong one.
  planProgress: planProgressSchema.optional(),
  // Whether Sona may be offered to this member: available, opted out by their
  // own choice, or restricted (hard-disabled). Derived on every read. Absent
  // when the server predates the field or the availability read failed; a
  // client must then keep its existing behaviour rather than assume either way.
  // The summary object is non-strict, so older clients parse it (ADR-135).
  aiAvailability: aiAvailabilitySchema.optional(),
  // Whether the member is in a programme now: `active` with at least one active
  // episode, `ended` with none active but at least one finished, `none`
  // otherwise. The check-in sheet uses it to say "your programme ended" rather
  // than "not enrolled yet". Optional because an older API omits it. A new
  // state is a breaking change for installed clients and needs a new endpoint.
  programmeEnrolment: z.enum(PROGRAMME_ENROLMENT_STATES).optional(),
});
export type HomeSummaryData = z.infer<typeof homeSummaryDataSchema>;

export const homeSummaryResponseSchema = apiEnvelope(homeSummaryDataSchema);
export type HomeSummaryResponse = z.infer<typeof homeSummaryResponseSchema>;
